/**
 * TableEmptyState
 *
 * Molecule that renders a custom empty state for data tables,
 * composed of an illustration, a bold title and a secondary description.
 *
 * ## Component Hierarchy
 * - **Level**: Molecule
 * - **Feature**: Users
 *
 * @module features/users/components/ui/molecules/TableEmptyState
 */
import React from 'react';

import emptyListIllustration from '@/assets/images/empty-list-illustration.svg';

import './TableEmptyState.css';

export interface TableEmptyStateProps {
  /** Bold heading shown below the illustration */
  title: string;
  /** Secondary description text */
  description: string;
}

/**
 * TableEmptyState Component
 *
 * @param props - Component properties
 * @returns Illustrated empty-state block for use inside Ant Design Table locale.emptyText
 *
 * @example
 * ```tsx
 * <TableEmptyState
 *   title={t('No users')}
 *   description={t('No users were found')}
 * />
 * ```
 */
export const TableEmptyState: React.FC<TableEmptyStateProps> = ({
  title,
  description,
}) => (
  <div className="idf-table-empty-state" role="status" aria-live="polite">
    <img
      src={emptyListIllustration}
      alt=""
      aria-hidden="true"
      className="idf-table-empty-state__illustration"
      width={138}
      height={96}
    />
    <p className="idf-table-empty-state__title">{title}</p>
    <p className="idf-table-empty-state__description">{description}</p>
  </div>
);

export default TableEmptyState;
